<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TechnicalPassportDimensions } from '../types'
import { sockDimensionFields } from '../sockPassportStructure'

const props = defineProps<{ dimensions: TechnicalPassportDimensions }>()
const { t } = useI18n()

const rows = computed(() =>
  sockDimensionFields.map((field) => ({
    ...field,
    label: t(field.labelKey),
    measurementLabel: t(field.measurementKey),
    tolerance: t(field.toleranceKey),
    unit: t(field.unitKey),
    valueCm: props.dimensions[field.key] == null ? '' : (Number(props.dimensions[field.key]) / 10).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1'),
  })),
)

function updateDimension(key: keyof TechnicalPassportDimensions, raw: string): void {
  if (!raw.trim()) {
    props.dimensions[key] = null
    return
  }
  const parsed = Number(raw)
  props.dimensions[key] = Number.isFinite(parsed) ? Math.round(parsed * 10 * 100) / 100 : props.dimensions[key]
}
</script>

<template>
  <section class="structure-card">
    <div class="structure-heading"><h3>{{ $t('engineering.passportStructure.finishedDimensions') }}</h3></div>
    <div class="dimension-table">
      <div class="table-head">
        <strong>{{ $t('engineering.passportForm.code') }}</strong>
        <strong>{{ $t('engineering.passportStructure.dimension') }}</strong>
        <strong>{{ $t('engineering.passportStructure.measurementHow') }}</strong>
        <strong>{{ $t('engineering.passportStructure.tolerance') }}</strong>
        <strong>{{ $t('engineering.passportStructure.measurementValue') }}</strong>
        <strong>{{ $t('engineering.passportStructure.unit') }}</strong>
      </div>
      <div v-for="field in rows" :key="field.key" class="dimension-row">
        <strong class="dimension-code">{{ field.code }}</strong>
        <div class="dimension-copy">
          <strong>{{ field.label }}</strong>
          <small>{{ $t(`engineering.structurePreview.dimension.${String(field.key)}`) }}</small>
        </div>
        <p class="dimension-measure">{{ field.measurementLabel }}</p>
        <span class="dimension-tolerance">{{ field.tolerance }}</span>
        <label class="dimension-input">
          <input
            :value="field.valueCm"
            min="0"
            step="0.01"
            type="number"
            @input="updateDimension(field.key, ($event.target as HTMLInputElement).value)"
          >
        </label>
        <span class="dimension-unit">{{ field.unit }}</span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.structure-card{padding:18px;border:1px solid #d7dfdc;background:#fff}
.structure-heading{display:flex;justify-content:space-between;margin-bottom:12px}
.structure-heading h3{margin:0}
.dimension-table{overflow:hidden;border:1px solid #d7dfdc}
.table-head,.dimension-row{display:grid;grid-template-columns:56px minmax(210px,1.25fr) minmax(240px,1.45fr) 88px minmax(110px,.7fr) 64px;gap:10px;align-items:center;padding:10px 12px}
.table-head{background:#f3f6f5;color:#53625d;font-size:11px;text-transform:uppercase}
.dimension-row{border-top:1px solid #e1e7e5}
.dimension-code{font-size:16px;color:#173454}
.dimension-copy{display:grid;gap:4px}
.dimension-copy strong{color:#173454;font-size:14px}
.dimension-copy small{color:#66726e;font-size:11px}
.dimension-measure{margin:0;color:#53625d;font-size:13px;line-height:1.35}
.dimension-tolerance,.dimension-unit{color:#53625d;font-size:13px;font-weight:700}
.dimension-input input{width:100%;padding:9px 10px;border:1px solid #b9c5c1;border-radius:4px;background:#eef8e7;font:inherit}
@media(max-width:900px){
  .table-head{display:none}
  .dimension-row{grid-template-columns:56px 1fr;gap:8px}
  .dimension-measure,.dimension-tolerance,.dimension-input,.dimension-unit{grid-column:2}
}
</style>
